import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, ShieldAlert, RefreshCw, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * One-tap sportsbook exposure peek for the admin home. Shows total open
 * liability + the 3 riskiest games right now, without opening the full
 * risk panel. Backed by GET /admin/sports-exposure.
 */
const SportsExposureQuickView = ({ onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/sports-exposure`, { withCredentials: true });
            setData(data);
        } catch { setData(null); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const games = (data?.by_game || []).slice(0, 3);
    const tickets = (data?.top_tickets || []).slice(0, 3);

    return (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
             onClick={onClose} data-testid="sports-exposure-quickview">
            <div className="w-full sm:max-w-md bg-[#12100a] border border-[#FFD700]/25 rounded-t-2xl sm:rounded-2xl p-4 sm:p-5 max-h-[85vh] overflow-y-auto"
                 onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-[#FFD700]/15 flex items-center justify-center shrink-0">
                            <ShieldAlert className="w-5 h-5 text-[#FFD700]" />
                        </div>
                        <div>
                            <h3 className="text-sm font-black text-[#FFD700] tracking-wide">SPORTSBOOK EXPOSURE</h3>
                            <p className="text-[10px] text-white/40">Potential payout if open bets win</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={load} className="p-2 rounded-lg hover:bg-white/10 text-white/60" data-testid="exposure-qv-refresh"><RefreshCw className="w-4 h-4" /></button>
                        <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-white/60" data-testid="exposure-qv-close"><X className="w-4 h-4" /></button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-white/50 text-sm py-8 text-center" data-testid="exposure-qv-loading">Loading exposure…</div>
                ) : !data ? (
                    <div className="text-white/50 text-sm py-8 text-center" data-testid="exposure-qv-error">Could not load exposure.</div>
                ) : (
                    <>
                        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                            <Stat label="Liability" value={fmt$(data.total?.liability)} color="text-[#ff5470]" testid="exposure-qv-liability" />
                            <Stat label="Open bets" value={data.total?.tickets ?? 0} color="text-white" testid="exposure-qv-tickets" />
                            <Stat label="Stake held" value={fmt$(data.total?.stake)} color="text-[#39FF14]" testid="exposure-qv-stake" />
                        </div>

                        <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-2">Top 3 riskiest games</div>
                        {games.length === 0 ? (
                            <div className="text-white/30 text-xs mb-4">No open game exposure.</div>
                        ) : (
                            <div className="space-y-1.5 mb-4">
                                {games.map((g, i) => (
                                    <div key={i} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2"
                                         data-testid={`exposure-qv-game-${i}`}>
                                        <div className="min-w-0">
                                            <div className="text-xs text-white/80 truncate">{g.key?.match || g.key?.event_id || 'Unknown event'}</div>
                                            <div className="text-[10px] text-white/30">{g.tickets} bet{g.tickets === 1 ? '' : 's'} · {g.customers} cust</div>
                                        </div>
                                        <span className="font-mono text-[#ff5470] font-bold text-sm shrink-0">{fmt$(g.liability)}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {tickets.length > 0 && (
                            <>
                                <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-2">Biggest single tickets</div>
                                <div className="space-y-1.5">
                                    {tickets.map((t, i) => (
                                        <div key={i} className="flex items-center justify-between gap-2 text-xs" data-testid={`exposure-qv-ticket-${i}`}>
                                            <span className="text-white/60 truncate max-w-[62%]">{t.selection || t.market || '—'}</span>
                                            <span className="font-mono text-[#ff5470] font-bold">{fmt$(t.liability)}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        <p className="mt-4 text-[10px] text-white/30 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" /> Open the Risk panel (Games → Availability) for the full breakdown.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

const Stat = ({ label, value, color, testid }) => (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-2.5">
        <div className="text-[9px] font-black uppercase tracking-wider text-white/40">{label}</div>
        <div className={`font-mono font-black text-sm mt-0.5 ${color}`} data-testid={testid}>{value}</div>
    </div>
);

export default SportsExposureQuickView;
